import { Controller } from "@hotwired/stimulus"
import { createConsumer } from "@rails/actioncable"
import { stream } from "@durable-streams/client"
import { renderStreamingMarkdown, renderMarkdown } from "../helpers/streaming_markdown"
import { orderMessages } from "../helpers/message_order"

export default class extends Controller {
  static targets = ["messagesContainer"]
  static values = {
    conversationId: String,
    currentUserId: String
  }

  connect() {
    this.activeStreams = new Map()
    this.wasDisconnected = false
    if (this.hasMessagesContainerTarget) {
      orderMessages(this.messagesContainerTarget.querySelector('#messages'))
    }
    this.scrollToBottom()
    this.subscribeToChannel()

    // Observe for new messages added via Turbo Streams
    // When a new incomplete message with data-stream-url appears, connect to its Durable Stream
    this.observer = new MutationObserver((mutations) => {
      const messagesDiv = this.messagesContainerTarget.querySelector('#messages')
      if (mutations.some(mutation => mutation.target === messagesDiv)) {
        orderMessages(messagesDiv)
      }
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (node.nodeType === Node.ELEMENT_NODE) {
            this.checkForStreamableMessage(node)
          }
        }
      }
      this.scrollToBottom()
    })

    if (this.hasMessagesContainerTarget) {
      this.observer.observe(this.messagesContainerTarget, {
        childList: true,
        subtree: true
      })
    }

    // On page load, connect to any existing incomplete messages with stream URLs
    this.connectToExistingStreams()
  }

  disconnect() {
    if (this.subscription) {
      this.subscription.unsubscribe()
    }
    if (this.observer) {
      this.observer.disconnect()
    }
    this.removeDisconnectionIndicator()
    // Cancel all active Durable Stream readers
    for (const [, abortController] of this.activeStreams) {
      abortController.abort()
    }
    this.activeStreams.clear()
  }

  // ActionCable subscription — still needed for Turbo Streams (message create/update broadcasts)
  subscribeToChannel() {
    if (!this.conversationIdValue) return

    const controller = this

    this.subscription = createConsumer().subscriptions.create(
      {
        channel: "ConversationChannel",
        conversation_id: this.conversationIdValue
      },
      {
        connected() {
          if (controller.wasDisconnected) {
            controller.removeDisconnectionIndicator()
            controller.syncAfterReconnection()
          }
          controller.wasDisconnected = false
        },
        disconnected() {
          controller.wasDisconnected = true
          controller.showDisconnectionIndicator()
        },
        rejected() {
          console.error("[ConversationChat] Subscription rejected by server")
        },
        received: (data) => this.handleReceived(data)
      }
    )
  }

  async syncAfterReconnection() {
    try {
      const lastTimestamp = this.getLastMessageTimestamp()

      const url = new URL(`/conversations/${this.conversationIdValue}/messages`, window.location.origin)
      if (lastTimestamp) {
        url.searchParams.set('after', lastTimestamp)
      }
      this.messagesContainerTarget.querySelectorAll('[data-message-id][data-complete="false"]').forEach(el => {
        url.searchParams.append('pending_message_ids[]', el.dataset.messageId)
      })

      const response = await fetch(url.toString(), {
        headers: {
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]')?.content
        }
      })

      if (!response.ok) return

      const data = await response.json()
      this.reconcileMessages(data.messages)
    } catch (error) {
      console.error("[ConversationChat] Error syncing after reconnection:", error)
    }
  }

  getLastMessageTimestamp() {
    const messagesDiv = this.messagesContainerTarget?.querySelector('#messages')
    if (!messagesDiv) return null

    // Pending messages are requested explicitly, even when older than this cursor.
    const completeMessages = messagesDiv.querySelectorAll('[data-complete="true"][data-created-at]')
    if (completeMessages.length === 0) return null

    let latest = null
    completeMessages.forEach(el => {
      const ts = el.dataset.createdAt
      if (!latest || ts > latest) {
        latest = ts
      }
    })
    return latest
  }

  reconcileMessages(serverMessages) {
    if (!serverMessages || serverMessages.length === 0) return

    const messagesDiv = this.messagesContainerTarget?.querySelector('#messages')
    if (!messagesDiv) return

    serverMessages.forEach(msg => {
      const existingEl = messagesDiv.querySelector(`[data-message-id="${msg.id}"]`)

      if (existingEl) {
        // Message exists in DOM -- update if server version is now complete
        if (msg.complete && existingEl.dataset.complete === "false") {
          const contentDiv = existingEl.querySelector('.prose') || existingEl.querySelector('[data-ai-content]')
          if (contentDiv && msg.content) {
            contentDiv.innerHTML = renderMarkdown(msg.content)
          }
          existingEl.dataset.complete = "true"

          const typingIndicator = existingEl.querySelector('.animate-bounce')?.parentElement
          if (typingIndicator) {
            typingIndicator.remove()
          }
        } else if (!msg.complete && msg.stream_url) {
          existingEl.dataset.streamUrl = msg.stream_url
          this.connectToStream(existingEl)
        }
      } else if (msg.html) {
        // ActionCable does not replay broadcasts missed while disconnected.
        this.appendMessage(msg.html)
      }
    })

    this.scrollToBottom()
  }

  showDisconnectionIndicator() {
    if (!this.hasMessagesContainerTarget) return
    if (document.getElementById('connection-status-indicator')) return

    const indicator = document.createElement('div')
    indicator.id = 'connection-status-indicator'
    indicator.className = 'fixed top-4 left-1/2 -translate-x-1/2 z-50 px-4 py-2 bg-warning-50 border border-warning-200 rounded-lg shadow-sm flex items-center gap-2 text-text-sm text-warning-700'
    indicator.innerHTML = `
      <span class="w-2 h-2 bg-warning-500 rounded-full animate-pulse"></span>
      <span>Reconnecting...</span>
    `
    document.body.appendChild(indicator)
  }

  removeDisconnectionIndicator() {
    const indicator = document.getElementById('connection-status-indicator')
    if (indicator) {
      indicator.remove()
    }
  }

  // Handle ActionCable messages — only Turbo Stream create/update events now
  // AI streaming is handled by Durable Streams, not ActionCable
  handleReceived(data) {
    if (data.type === 'message' || data.type === 'create') {
      if (data.html) {
        this.appendMessage(data.html)
      }
    } else if (data.type === 'update') {
      if (data.html) {
        this.replaceMessage(data.message_id, data.html)
      } else if (data.content) {
        this.updateMessageContent(data.message_id, data.content, data.complete)
      }
    }
  }

  // Check if a newly added DOM node is a message that needs a Durable Stream connection
  checkForStreamableMessage(node) {
    // Check the node itself
    if (node.dataset?.streamUrl && node.dataset?.complete === "false") {
      this.connectToStream(node)
      return
    }
    // Check children (Turbo Stream may wrap the message element)
    const streamableEl = node.querySelector?.('[data-stream-url][data-complete="false"]')
    if (streamableEl) {
      this.connectToStream(streamableEl)
    }
  }

  // On page load, find any incomplete messages with stream URLs and connect
  connectToExistingStreams() {
    if (!this.hasMessagesContainerTarget) return

    const incompleteMessages = this.messagesContainerTarget.querySelectorAll(
      '[data-stream-url][data-complete="false"]'
    )
    incompleteMessages.forEach((el) => this.connectToStream(el))
  }

  // Connect to a Durable Stream for a specific message element
  async connectToStream(messageEl) {
    const streamUrl = messageEl.dataset.streamUrl
    const messageId = messageEl.dataset.messageId

    if (!streamUrl || !messageId) return
    // Don't double-connect
    if (this.activeStreams.has(messageId)) return

    const abortController = new AbortController()
    this.activeStreams.set(messageId, abortController)

    try {
      const res = await stream({
        url: streamUrl,
        offset: "-1", // Read from beginning (enables catch-up on refresh)
        live: "sse",
        json: true,
        signal: abortController.signal
      })

      res.subscribeJson(async (batch) => {
        for (const item of batch.items) {
          this.updateStreamingMessage(messageId, item.content, item.complete)
        }
      })

      // Clean up when the stream session closes (stream closed, cancelled, or error)
      res.closed.then(() => {
        this.activeStreams.delete(messageId)
      })
    } catch (error) {
      if (error.name === 'AbortError') return // Expected on disconnect
      console.error(`Durable Stream error for message ${messageId}:`, error)
      this.activeStreams.delete(messageId)
    }
  }

  appendMessage(html) {
    if (!this.hasMessagesContainerTarget) return

    const messagesDiv = this.messagesContainerTarget.querySelector('#messages')
    if (messagesDiv) {
      messagesDiv.insertAdjacentHTML('beforeend', html)
      orderMessages(messagesDiv)
    }
    this.scrollToBottom()
  }

  replaceMessage(messageId, html) {
    const messageEl = document.getElementById(`message_${messageId}`)
    if (messageEl) {
      messageEl.outerHTML = html
    }
  }

  updateStreamingMessage(messageId, content, complete) {
    const messageEl = document.querySelector(`[data-message-id="${messageId}"]`)
    if (messageEl) {
      const contentDiv = messageEl.querySelector('.prose') || messageEl.querySelector('[data-ai-content]')
      if (contentDiv) {
        contentDiv.innerHTML = complete
          ? renderMarkdown(content)
          : renderStreamingMarkdown(content)
      }

      messageEl.dataset.complete = complete

      if (complete) {
        const typingIndicator = messageEl.querySelector('.animate-bounce')?.parentElement
        if (typingIndicator) {
          typingIndicator.remove()
        }
      }

      this.scrollToBottom()
    }
  }

  updateMessageContent(messageId, content, complete) {
    const messageEl = document.querySelector(`[data-message-id="${messageId}"]`)
    if (messageEl) {
      const contentDiv = messageEl.querySelector('.prose') || messageEl.querySelector('.text-sm') || messageEl.querySelector('[data-ai-content]')
      if (contentDiv) {
        contentDiv.innerHTML = renderMarkdown(content)
      }
      if (complete !== undefined) {
        messageEl.dataset.complete = complete
      }
      this.scrollToBottom()
    }
  }

  scrollToBottom() {
    if (this.hasMessagesContainerTarget) {
      requestAnimationFrame(() => {
        this.messagesContainerTarget.scrollTop = this.messagesContainerTarget.scrollHeight
      })
    }
  }
}
